import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { JobRecord } from "@rebuild/shared/models";
import { JOB_KEYS } from "./queryKeys";
import { useJob } from "./useJob";

/** Give up waiting on a tracked job after this long and treat the outcome as indeterminate. */
const DEFAULT_SAFETY_TIMEOUT_MS = 5 * 60 * 1000;

type TrackedJobAbandonReason = "timeout" | "status-unavailable";

interface UseTrackedWorkerJobOptions<TContext> {
  /** Used to wake the estimate-wide job list; tracking is inert until this is defined. */
  estimateId: string | undefined;
  onSucceeded: (context: TContext) => void;
  onFailed: (context: TContext, error: JobRecord["error"]) => void;
  /**
   * The job may still be running, so this is neither success nor failure. Callers should refresh what
   * they can and tell the user the work is unfinished rather than claiming an outcome — in particular,
   * they should not roll back optimistic state the way `onFailed` does.
   */
  onAbandoned: (context: TContext, reason: TrackedJobAbandonReason) => void;
  safetyTimeoutMs?: number;
}

interface TrackedWorkerJob<TContext> {
  track: (jobId: string, context: TContext) => void;
  isTracking: boolean;
  cancel: () => void;
}

/**
 * Follows one enqueued worker job to a terminal outcome and reports it exactly once.
 *
 * Shared by every flow that hands expensive work to the BullMQ worker and holds UI open until it lands
 * (TPA/carrier rules, Cat-3 line-item resync). The outcomes are deliberately three-way: a job that
 * failed is not the same as one we stopped being able to read, and conflating them makes the UI claim
 * things that never happened.
 *
 * `context` is whatever the caller needs handed back when the job resolves — a snapshot to diff
 * against, say. Pass `void` when there is nothing to carry.
 */
export function useTrackedWorkerJob<TContext = void>({
  estimateId,
  onSucceeded,
  onFailed,
  onAbandoned,
  safetyTimeoutMs = DEFAULT_SAFETY_TIMEOUT_MS,
}: UseTrackedWorkerJobOptions<TContext>): TrackedWorkerJob<TContext> {
  const queryClient = useQueryClient();

  const [trackedJobId, setTrackedJobId] = useState<string | undefined>(undefined);
  const [isTracking, setIsTracking] = useState(false);
  const pendingRef = useRef(false);
  const contextRef = useRef<TContext | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Held in a ref so the resolve effect below doesn't re-run every time a caller's callback identity
  // churns — it must fire on job state, not on render.
  const handlersRef = useRef({ onSucceeded, onFailed, onAbandoned });
  handlersRef.current = { onSucceeded, onFailed, onAbandoned };

  const { isTerminal, failed, error, statusUnavailable } = useJob(trackedJobId);

  const clearSafetyTimer = useCallback(() => {
    if (safetyTimerRef.current !== null) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearSafetyTimer, [clearSafetyTimer]);

  // Tear down the pending state and hand back the caller's context. Returns null when there is nothing
  // pending, so whichever outcome fires first wins and the rest are no-ops.
  const resolvePending = useCallback((): { context: TContext } | null => {
    if (!pendingRef.current) return null;
    pendingRef.current = false;
    setIsTracking(false);
    setTrackedJobId(undefined);
    clearSafetyTimer();

    const context = contextRef.current as TContext;
    contextRef.current = null;
    // Let the estimate-wide poll pick up the job's terminal status for its own consumers.
    if (estimateId) {
      queryClient.invalidateQueries({ queryKey: JOB_KEYS.forEstimate(estimateId) });
    }
    return { context };
  }, [clearSafetyTimer, estimateId, queryClient]);

  const abandon = useCallback(
    (reason: TrackedJobAbandonReason) => {
      const resolved = resolvePending();
      if (!resolved) return;
      handlersRef.current.onAbandoned(resolved.context, reason);
    },
    [resolvePending],
  );

  const track = useCallback(
    (jobId: string, context: TContext) => {
      contextRef.current = context;
      pendingRef.current = true;
      setIsTracking(true);
      setTrackedJobId(jobId);
      // Wake the estimate-wide job list: it stops polling whenever nothing is active, so without this
      // its consumers keep reading a pre-enqueue snapshot (no active jobs, an old failure still listed)
      // for the entire run of this job.
      if (estimateId) {
        queryClient.invalidateQueries({ queryKey: JOB_KEYS.forEstimate(estimateId) });
      }
      clearSafetyTimer();
      safetyTimerRef.current = setTimeout(() => abandon("timeout"), safetyTimeoutMs);
    },
    [abandon, clearSafetyTimer, estimateId, queryClient, safetyTimeoutMs],
  );

  const cancel = useCallback(() => {
    resolvePending();
  }, [resolvePending]);

  useEffect(() => {
    if (!pendingRef.current || !trackedJobId) return;
    // Polling gave up on the status endpoint, so we no longer know what the job did. Resolve now instead
    // of holding the user until the safety timeout — but as indeterminate, not as a failure, since an
    // unreadable status says nothing about the job's outcome.
    if (statusUnavailable) {
      abandon("status-unavailable");
      return;
    }
    if (!isTerminal) return;

    const resolved = resolvePending();
    if (!resolved) return;
    if (failed) {
      handlersRef.current.onFailed(resolved.context, error);
    } else {
      handlersRef.current.onSucceeded(resolved.context);
    }
  }, [trackedJobId, isTerminal, failed, error, statusUnavailable, abandon, resolvePending]);

  return { track, isTracking, cancel };
}
